import * as React from "react";
import { Badge, Text, makeStyles, tokens } from "@fluentui/react-components";

import {
  SOURCE_BODY,
  SOURCE_SUBJECT,
  confidenceBand,
  wasEdited,
  type ConfidenceBand,
  type SuggestedField,
} from "../lib/suggestion";

/*
 * Where a prefilled value came from and how sure we are of it.
 *
 * Today this always reads "From the subject · High confidence", because a
 * substring is not a guess. Painting it anyway is the point: when a model
 * starts producing these values it will hand over a 0.62 and a quote from the
 * body, and this component already renders both. The client does not change
 * when the prefill gets smarter — which is the whole bet.
 */

const useStyles = makeStyles({
  row: { display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", marginTop: "4px" },
  source: { color: tokens.colorNeutralForeground3 },
});

const BAND_LABEL: Record<ConfidenceBand, string> = {
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
};

const BAND_COLOR: Record<ConfidenceBand, "informative" | "warning" | "danger"> = {
  high: "informative",
  medium: "warning",
  low: "danger",
};

/**
 * Turns a source into something a person can read.
 *
 * The two known values are what a substring can cite. Anything else is treated
 * as the verbatim quote a model will put here, and shown as one — which is why
 * this is not an exhaustive switch.
 */
function describeSource(source: string): string {
  switch (source) {
    case SOURCE_SUBJECT:
      return "From the subject";
    case SOURCE_BODY:
      return "From the email body";
    default: {
      const quote = source.replace(/\s+/g, " ").trim();
      return quote.length > 80 ? `From: “${quote.slice(0, 79)}…”` : `From: “${quote}”`;
    }
  }
}

export interface SuggestionHintProps {
  /** The proposed value, or undefined before the email has been read. */
  field: SuggestedField | undefined;
  /** What is in the form now, so an edit can be marked as one. */
  current: string;
}

export const SuggestionHint: React.FC<SuggestionHintProps> = ({ field, current }) => {
  const styles = useStyles();

  if (!field) {
    return null;
  }

  // Nothing was proposed. Saying so is worth a line: on the project it tells
  // the user the choice is theirs to make, and when extraction arrives it is
  // how a field the email did not answer announces itself.
  if (field.value === null) {
    return (
      <div className={styles.row}>
        <Text size={200} className={styles.source}>
          Not found in this email
        </Text>
      </div>
    );
  }

  const band = confidenceBand(field.confidence);
  const edited = wasEdited(field, current);

  return (
    <div className={styles.row}>
      <Badge appearance="tint" color={BAND_COLOR[band]} size="small">
        {BAND_LABEL[band]}
      </Badge>
      {field.source && (
        <Text size={200} className={styles.source}>
          {describeSource(field.source)}
        </Text>
      )}
      {edited && (
        <Badge appearance="tint" color="success" size="small">
          Edited
        </Badge>
      )}
    </div>
  );
};
